import {
  ActionIcon,
  Flex,
  Grid,
  NumberInput,
  Select,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { IconTrash } from "@tabler/icons-react";
import { SOItemFormValues } from "constants/types/salesOrder";
import { ChangeEvent, memo, useCallback } from "react";
import { calcItemTotal, formatCurrency } from "../../utils";
import CustomText from "components/ui/CustomText/CustomText";

type ProductOption = { label: string; value: string; disabled?: boolean };
type ProductOptionGroup = { group: string; items: ProductOption[] };
type SOItemField = keyof SOItemFormValues;
type SOEditableField = "quantity" | "actualPrice" | "productName";

interface SalesOrderItemRowProps {
  index: number;
  item: SOItemFormValues;
  itemError?: Partial<Record<SOItemField, string>>;
  itemTouched?: Partial<Record<SOItemField, boolean>>;
  productOptions: ProductOptionGroup[];
  maxQuantity?: number;
  onProductChange: (index: number, productId: string | null) => void;
  onFieldChange: (index: number, field: SOEditableField, value: number | string) => void;
  onFieldBlur: (index: number, field: SOItemField) => void;
  onRemove: (index: number) => void;
  canRemove: boolean;
}

const spanThird = { base: 12, md: 4 };
const spanQuarter = { base: 12, sm: 6, md: 4 };

const SalesOrderItemRow = memo(
  ({
    index,
    item,
    itemError,
    itemTouched,
    productOptions,
    maxQuantity,
    onProductChange,
    onFieldChange,
    onFieldBlur,
    onRemove,
    canRemove,
  }: SalesOrderItemRowProps) => {
    const itemTotal = calcItemTotal(item.quantity, item.actualPrice);

    const getItemError = useCallback(
      (field: SOItemField) =>
        itemTouched?.[field] && itemError?.[field] ? itemError[field] : null,
      [itemError, itemTouched],
    );

    const handleProductChange = useCallback(
      (productId: string | null) => onProductChange(index, productId),
      [onProductChange, index],
    );
    const handleProductBlur = useCallback(
      () => onFieldBlur(index, "productId"),
      [onFieldBlur, index],
    );
    const handleProductNameChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) =>
        onFieldChange(index, "productName", event.currentTarget.value),
      [onFieldChange, index],
    );
    const handleProductNameBlur = useCallback(
      () => onFieldBlur(index, "productName"),
      [onFieldBlur, index],
    );
    const handleQuantityChange = useCallback(
      (value: number | string) => onFieldChange(index, "quantity", value),
      [onFieldChange, index],
    );
    const handleQuantityBlur = useCallback(
      () => onFieldBlur(index, "quantity"),
      [onFieldBlur, index],
    );
    const handleActualPriceChange = useCallback(
      (value: number | string) => onFieldChange(index, "actualPrice", value),
      [onFieldChange, index],
    );
    const handleActualPriceBlur = useCallback(
      () => onFieldBlur(index, "actualPrice"),
      [onFieldBlur, index],
    );
    const handleRemove = useCallback(() => onRemove(index), [onRemove, index]);

    return (
      <Stack gap="xs">
        <Flex align="center" justify="space-between">
          <Flex align="center" gap={10}>
            <CustomText
              variant="product-label"
              children={`Sản phẩm ${index + 1}`}
            />
            <ActionIcon
              color="red"
              variant="subtle"
              onClick={handleRemove}
              disabled={!canRemove}
              aria-label="Xóa dòng"
            >
              <IconTrash size={14} />
            </ActionIcon>
          </Flex>

          <Flex align="center" gap={6}>
            <Text size="sm" c="dimmed">Thành tiền:</Text>
            <Text fw={700} size="sm">{formatCurrency(itemTotal)}</Text>
          </Flex>
        </Flex>

        <Grid align="flex-start">
          <Grid.Col span={spanThird}>
            <Select
              label="Sản phẩm"
              placeholder="Chọn sản phẩm"
              withAsterisk
              data={productOptions}
              value={item.productId || null}
              onChange={handleProductChange}
              onBlur={handleProductBlur}
              error={getItemError("productId")}
              searchable
            />
          </Grid.Col>

          <Grid.Col span={spanThird}>
            <TextInput
              label="Tên trên đơn"
              placeholder="Tên hiển thị trên đơn và phiếu in"
              value={item.productName}
              onChange={handleProductNameChange}
              onBlur={handleProductNameBlur}
              error={getItemError("productName")}
            />
          </Grid.Col>

          <Grid.Col span={spanQuarter}>
            <TextInput
              label="Đơn vị"
              placeholder="—"
              readOnly
              value={item.productUnit || ""}
            />
          </Grid.Col>

          <Grid.Col span={spanQuarter}>
            <NumberInput
              label="Số lượng"
              placeholder="0"
              withAsterisk
              min={1}
              max={maxQuantity}
              value={item.quantity}
              onChange={handleQuantityChange}
              onBlur={handleQuantityBlur}
              error={getItemError("quantity")}
            />
          </Grid.Col>

          <Grid.Col span={spanQuarter}>
            <NumberInput
              label="Giá gốc"
              placeholder="0"
              withAsterisk
              min={0}
              thousandSeparator=","
              value={item.initialPrice}
              error={getItemError("initialPrice")}
              disabled
            />
          </Grid.Col>

          <Grid.Col span={spanQuarter}>
            <NumberInput
              label="Giá bán"
              placeholder="0"
              withAsterisk
              min={0}
              thousandSeparator=","
              value={item.actualPrice}
              onChange={handleActualPriceChange}
              onBlur={handleActualPriceBlur}
              error={getItemError("actualPrice")}
            />
          </Grid.Col>
        </Grid>
      </Stack>
    );
  },
);

SalesOrderItemRow.displayName = "SalesOrderItemRow";

export default SalesOrderItemRow;
